import { message } from 'antd';
import * as Sentry from '@sentry/react';
// html2canvas and jsPDF both ship as dependencies of html2pdf.js (already in the lockfile).
// We drive them directly instead of using html2pdf's one-shot pipeline because we need to
// decide the page break positions ourselves: html2pdf slices the captured canvas at fixed
// page heights with no awareness of where a chart or table card begins and ends, which is what
// cut the charts in half across pages. Here we capture the page once and then place the slices
// so that no card that fits within a page is ever split across two pages.
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

// A4 landscape, millimetres. Margins are applied on every side and the captured image is placed
// at the full content width, so vertical canvas pixels map to millimetres by the same ratio.
const PAGE_WIDTH_MM = 297;
const PAGE_HEIGHT_MM = 210;
const PAGE_MARGIN_MM = 10;
const CONTENT_WIDTH_MM = PAGE_WIDTH_MM - PAGE_MARGIN_MM * 2;
const CONTENT_HEIGHT_MM = PAGE_HEIGHT_MM - PAGE_MARGIN_MM * 2;

const CAPTURE_SCALE = 2;
const IMAGE_FORMAT = 'JPEG';
const IMAGE_MIME = 'image/jpeg';
const IMAGE_QUALITY = 0.95;

// When a page boundary lands inside a card we pull the cut up to the card's top so the whole
// card moves to the next page. Skip that pull-back if it would leave the current page less than
// this fraction filled, so a card that is itself ~one page tall doesn't leave a near-blank page.
const MIN_PAGE_FILL_RATIO = 0.12;
const EPSILON_PX = 1;
// Drop a final trailing slice this small (source-canvas px) rather than emit a near-blank page
// for it. Kept tiny so it can only ever be capture rounding / whitespace, never real content —
// even a single table row is far taller than this once scaled by CAPTURE_SCALE.
const MIN_TRAILING_SLICE_PX = 4;

type CardBoundsCss = { topCss: number; bottomCss: number };
type CardBoundsPx = { top: number; bottom: number };

/**
 * Exports the analytics page identified by `rootSelector` as a landscape A4 PDF, keeping each
 * chart/table card whole instead of letting it be sliced across a page boundary.
 *
 * Why we capture the whole page in one pass (rather than each card individually): the analytics
 * pages also render content that lives outside any card — the KPI page's facility-name header,
 * the selected-filter pills, multi-column card rows — and a per-card capture would drop or
 * reflow those. Capturing the root preserves the exact on-screen layout; we only change where
 * the page breaks fall.
 *
 * `options.additionalProtectedSelectors` lists extra CSS selectors whose matched elements should
 * also be kept whole across page breaks (e.g. individual table rows for pages whose content cards
 * span multiple pages and would otherwise cut rows in half).
 */
export const exportAnalyticsPageAsPdf = async (
	rootSelector: string,
	filename: string,
	options?: { additionalProtectedSelectors?: string[] }
): Promise<void> => {
	const root = document.querySelector(rootSelector) as HTMLElement | null;
	if (!root) {
		message.error('Could not find page content to export.');
		return;
	}

	const hidePdfLoadingMessage = message.loading('Generating PDF…', 0);

	try {
		// Measure every top-level card's vertical span BEFORE capture. Measurements come from the
		// live DOM; the capture preserves the same layout — chart <canvas> elements are swapped for
		// equal-height <img> elements and hidden controls keep their box via visibility:hidden — so
		// these bounds map 1:1 onto the rows of the captured canvas.

		const pdfOnlyElements = Array.from(
			root.querySelectorAll('.analytics-pdf-only')
		) as HTMLElement[];
		const savedPdfOnlyDisplayValues = pdfOnlyElements.map((el) => el.style.display);
		pdfOnlyElements.forEach((el) => {
			el.style.display = 'block';
		});
		let cardBoundsCss: CardBoundsCss[];
		let scrollableTableWidths: number[];
		let captureWidthCss: number;
		try {
			cardBoundsCss = getProtectedElementBounds(
				root,
				options && options.additionalProtectedSelectors
			);
			// Each antd table sits inside an overflow-x auto wrapper; when the columns sum wider
			// than the viewport, html2canvas captures only the columns currently in the horizontal
			// scroll and drops the rest. Record each wrapper's natural scrollWidth here so the
			// clone can be pinned to it in onclone and captured at the expanded width.
			const rootLeft = root.getBoundingClientRect().left;
			const scrollableWrappers = Array.from(
				root.querySelectorAll('.ant-table-content')
			) as HTMLElement[];
			scrollableTableWidths = scrollableWrappers.map((wrapper) => wrapper.scrollWidth);
			captureWidthCss = scrollableWrappers.reduce((max, wrapper) => {
				const offsetFromRoot = wrapper.getBoundingClientRect().left - rootLeft;
				return Math.max(max, offsetFromRoot + wrapper.scrollWidth);
			}, root.getBoundingClientRect().width);
		} finally {
			pdfOnlyElements.forEach((el, index) => {
				el.style.display = savedPdfOnlyDisplayValues[index];
			});
		}

		const needsWidening = captureWidthCss > root.getBoundingClientRect().width + EPSILON_PX;
		const html2canvasOptions: any = {
			scale: CAPTURE_SCALE,
			useCORS: true,
			logging: false,
			backgroundColor: '#ffffff',
			onclone: (clonedDoc: Document) => {
				replaceChartCanvasesWithImages(clonedDoc, rootSelector);
				hideNonPrintableElements(clonedDoc, rootSelector);
				showPdfOnlyElements(clonedDoc, rootSelector);
				if (needsWidening) {
					expandScrollableTablesInClone(clonedDoc, rootSelector, scrollableTableWidths);
				}
			},
		};
		if (needsWidening) {
			// `width` sets the capture rectangle; `windowWidth` forces the clone to lay out at that
			// width so ancestor flex/grid constraints don't reflow the widened tables back to the
			// live viewport's width. Only applied when a table actually needs widening — passing
			// these on pages without a scrollable table would render the clone at a different
			// viewport width than live and re-trigger responsive breakpoints.
			html2canvasOptions.width = captureWidthCss;
			html2canvasOptions.windowWidth = captureWidthCss;
		}
		const canvas = await html2canvas(root, html2canvasOptions);

		const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
		renderCanvasAcrossPages(pdf, canvas, cardBoundsCss);
		pdf.save(filename);
	} catch (error) {
		Sentry.captureException(error);
		message.error('Could not generate the PDF. Please try again.');
	} finally {
		hidePdfLoadingMessage();
	}
};

/**
 * Vertical bounds (relative to the root's top, in CSS pixels) of elements that should not be
 * split across page boundaries. Always includes the outermost `.ant-card` elements (cards nested
 * inside another card are dropped — the ancestor already protects them). Any selectors in
 * `additionalSelectors` are also included, so callers can protect individual table rows or other
 * fine-grained elements inside cards that span multiple pages.
 */
const getProtectedElementBounds = (
	root: HTMLElement,
	additionalSelectors?: string[]
): CardBoundsCss[] => {
	const rootTop = root.getBoundingClientRect().top;

	const allCards = Array.from(root.querySelectorAll('.ant-card')) as HTMLElement[];
	const topLevelCards = allCards.filter(
		(card) => !allCards.some((other) => other !== card && other.contains(card))
	);
	const cardBounds: CardBoundsCss[] = topLevelCards.map((card) => {
		const rect = card.getBoundingClientRect();
		return { topCss: rect.top - rootTop, bottomCss: rect.bottom - rootTop };
	});

	const extraBounds: CardBoundsCss[] = (additionalSelectors || []).flatMap((selector) =>
		(Array.from(root.querySelectorAll(selector)) as HTMLElement[]).map((el) => {
			const rect = el.getBoundingClientRect();
			return { topCss: rect.top - rootTop, bottomCss: rect.bottom - rootTop };
		})
	);

	return [...cardBounds, ...extraBounds];
};

/**
 * BizCharts/G2 render charts into <canvas> elements, which html2canvas captures unreliably (it
 * ignores CSS transforms applied to canvas). Swap each cloned canvas for an <img> built from the
 * live canvas's pixels, keeping the same height so the layout — and therefore our measured card
 * bounds — stays aligned.
 */
const replaceChartCanvasesWithImages = (clonedDoc: Document, rootSelector: string): void => {
	const liveCanvases = Array.from(
		document.querySelectorAll(`${rootSelector} canvas`)
	) as HTMLCanvasElement[];
	const clonedCanvases = Array.from(
		clonedDoc.querySelectorAll(`${rootSelector} canvas`)
	) as HTMLCanvasElement[];

	clonedCanvases.forEach((clonedCanvas, index) => {
		if (index >= liveCanvases.length) {
			return;
		}
		try {
			const image = clonedDoc.createElement('img') as HTMLImageElement;
			image.src = liveCanvases[index].toDataURL('image/png');
			image.style.display = 'block';
			image.style.width = '100%';
			image.style.height =
				liveCanvases[index].style.height || liveCanvases[index].offsetHeight + 'px';
			if (clonedCanvas.parentNode) {
				clonedCanvas.parentNode.replaceChild(image, clonedCanvas);
			}
		} catch (_error) {
			// Cross-origin tainted canvas — leave the original canvas in place.
		}
	});
};

/**
 * Hide the action buttons (filter/download/print) and per-card controls in the clone. We use
 * visibility:hidden rather than display:none so the elements keep occupying their layout box;
 * removing the box would shift everything below it and break the alignment between the captured
 * canvas and the card bounds we measured from the live DOM.
 */
const hideNonPrintableElements = (clonedDoc: Document, rootSelector: string): void => {
	const selector = [
		`${rootSelector} .analytics-pdf-hide-in-print`,
		`${rootSelector} .ant-card-extra button`,
	].join(', ');
	(Array.from(clonedDoc.querySelectorAll(selector)) as HTMLElement[]).forEach((element) => {
		element.style.visibility = 'hidden';
	});
};

/**
 * Make elements marked `analytics-pdf-only` visible in the clone. These elements have
 * `display:none` in the live DOM so they don't affect the normal UI, but they should appear
 * in the exported PDF (e.g. page-title headings that the OverviewShell header normally provides
 * but which fall outside the captured root selector).
 */
const showPdfOnlyElements = (clonedDoc: Document, rootSelector: string): void => {
	(
		Array.from(clonedDoc.querySelectorAll(`${rootSelector} .analytics-pdf-only`)) as HTMLElement[]
	).forEach((element) => {
		element.style.display = 'block';
	});
};

/**
 * Lift the horizontal-scroll clipping on each antd table wrapper in the clone and pin it to the
 * live element's natural width. Without this, the clone still has `overflow-x: auto` on
 * `.ant-table-content` and html2canvas — even at an expanded capture width — renders the wrapper
 * at its viewport-derived width and clips the off-viewport columns. The clone's own `scrollWidth`
 * is unreliable before layout completes, so the live measurements are passed in by ordinal
 * position (the clone preserves the source order of matched elements).
 */
const expandScrollableTablesInClone = (
	clonedDoc: Document,
	rootSelector: string,
	liveTableWidths: number[]
): void => {
	const clonedWrappers = Array.from(
		clonedDoc.querySelectorAll(`${rootSelector} .ant-table-content`)
	) as HTMLElement[];
	clonedWrappers.forEach((wrapper, index) => {
		if (index >= liveTableWidths.length) return;
		wrapper.style.width = `${liveTableWidths[index]}px`;
		wrapper.style.overflowX = 'visible';
	});
	const clonedRoot = clonedDoc.querySelector(rootSelector) as HTMLElement | null;
	if (clonedRoot) {
		// Any ancestor still clipping horizontally would re-clip the wider table.
		clonedRoot.style.overflowX = 'visible';
	}
};

/**
 * Slice the captured canvas into A4-landscape pages, pulling each page break up to a card's top
 * whenever the naive break would fall inside a card that fits within a single page.
 */
const renderCanvasAcrossPages = (
	pdf: jsPDF,
	canvas: HTMLCanvasElement,
	cardBoundsCss: CardBoundsCss[]
): void => {
	const canvasWidthPx = canvas.width;
	const canvasHeightPx = canvas.height;
	if (!canvasWidthPx || !canvasHeightPx) {
		return;
	}

	// html2canvas draws the content with ctx.scale(CAPTURE_SCALE, CAPTURE_SCALE), so a CSS pixel
	// maps to exactly CAPTURE_SCALE source-canvas pixels. (Reconstructing the ratio from
	// canvas.width / cssWidth would be off by a sub-pixel because html2canvas ceils the element
	// size then floors the backing-store dimensions — that error accumulates over a tall page.)
	const cardBoundsPx: CardBoundsPx[] = cardBoundsCss.map((bounds) => ({
		top: bounds.topCss * CAPTURE_SCALE,
		bottom: bounds.bottomCss * CAPTURE_SCALE,
	}));

	// One printable page expressed in source-canvas pixels.
	const pxPerMm = canvasWidthPx / CONTENT_WIDTH_MM;
	const pagePx = CONTENT_HEIGHT_MM * pxPerMm;
	const minPageFillPx = pagePx * MIN_PAGE_FILL_RATIO;

	const pageCanvas = document.createElement('canvas');
	const pageContext = pageCanvas.getContext('2d');
	pageCanvas.width = canvasWidthPx;

	let sliceTopPx = 0;
	let isFirstPage = true;

	while (sliceTopPx < canvasHeightPx - EPSILON_PX) {
		const naiveBottomPx = sliceTopPx + pagePx;
		let sliceBottomPx = Math.min(naiveBottomPx, canvasHeightPx);

		if (naiveBottomPx < canvasHeightPx) {
			const pullTopPx = findBreakAvoidingCardCut(cardBoundsPx, sliceTopPx, naiveBottomPx, pagePx);
			if (pullTopPx !== null && pullTopPx > sliceTopPx + minPageFillPx) {
				sliceBottomPx = pullTopPx;
			}
		}

		const sliceHeightPx = Math.max(1, Math.round(sliceBottomPx - sliceTopPx));

		// Skip a tiny trailing remainder so we don't emit an otherwise-blank final page.
		const isFinalSlice = sliceBottomPx >= canvasHeightPx - EPSILON_PX;
		if (isFinalSlice && !isFirstPage && sliceHeightPx <= MIN_TRAILING_SLICE_PX) {
			break;
		}

		pageCanvas.height = sliceHeightPx;
		if (pageContext) {
			pageContext.fillStyle = '#ffffff';
			pageContext.fillRect(0, 0, pageCanvas.width, sliceHeightPx);
			pageContext.drawImage(
				canvas,
				0,
				Math.round(sliceTopPx),
				canvasWidthPx,
				sliceHeightPx,
				0,
				0,
				canvasWidthPx,
				sliceHeightPx
			);
		}

		const sliceHeightMm = sliceHeightPx / pxPerMm;
		const imageData = pageCanvas.toDataURL(IMAGE_MIME, IMAGE_QUALITY);
		if (!isFirstPage) {
			pdf.addPage();
		}
		pdf.addImage(
			imageData,
			IMAGE_FORMAT,
			PAGE_MARGIN_MM,
			PAGE_MARGIN_MM,
			CONTENT_WIDTH_MM,
			sliceHeightMm
		);

		isFirstPage = false;
		sliceTopPx = sliceBottomPx;
	}
};

/**
 * If the page break at `naiveBottomPx` would cut through one or more cards that start on the
 * current page and fit within a single page, return the topmost such card's top so the break can
 * be pulled up to it. Returns null when nothing needs avoiding (clean break in a gap, or the only
 * straddling card is taller than a page and therefore cannot be kept whole regardless).
 */
const findBreakAvoidingCardCut = (
	cardBoundsPx: CardBoundsPx[],
	sliceTopPx: number,
	naiveBottomPx: number,
	pagePx: number
): number | null => {
	let pullTopPx: number | null = null;
	cardBoundsPx.forEach((bounds) => {
		const startsOnThisPage = bounds.top > sliceTopPx + EPSILON_PX;
		const crossesTheBreak =
			bounds.top < naiveBottomPx - EPSILON_PX && bounds.bottom > naiveBottomPx + EPSILON_PX;
		const fitsWithinOnePage = bounds.bottom - bounds.top <= pagePx + EPSILON_PX;
		if (startsOnThisPage && crossesTheBreak && fitsWithinOnePage) {
			pullTopPx = pullTopPx === null ? bounds.top : Math.min(pullTopPx, bounds.top);
		}
	});
	return pullTopPx;
};
